import { createFileRoute, Link } from "@tanstack/react-router";
import React, { useState } from "react";
import {
  ArrowLeft,
  BadgeCheck,
  Calendar,
  Check,
  CircleAlert,
  Copy,
  Download,
  GraduationCap,
  IdCard,
  Loader2,
} from "lucide-react";
import { useQuery } from "@tanstack/react-query";
import apiClient from "@/lib/api-client";
import { motion, AnimatePresence } from "framer-motion";
import BrandMark from "@/components/layout/BrandMark";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";

export const Route = createFileRoute("/verify/$code")({
  component: CertificateVerificationPage,
});

type CertData = {
  cert: {
    id: number;
    cert_number: string;
    student_name: string;
    course: string;
    university: string;
    issue_date: string;
    status: string;
    user_id: number;
    template_id: number;
  };
  template: {
    id: number;
    name: string;
    type: string;
    description: string;
    border_color: string;
    accent_color: string;
    badge_icon: string;
  };
};

const API_URL = import.meta.env.VITE_API_URL || "http://localhost:8080/api/v1";

function CertificateVerificationPage() {
  const { code } = Route.useParams();
  const [copied, setCopied] = useState(false);

  const {
    data,
    isLoading,
    isError,
  } = useQuery<CertData>({
    queryKey: ["verify-certificate", code],
    queryFn: async () => {
      const res = await apiClient.get(`/public/certificates/verify/${code}`);
      return (res.data as any)?.data || res.data;
    },
    retry: false,
  });

  const handleCopy = () => {
    navigator.clipboard.writeText(window.location.href);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const handleDownloadPDF = () => {
    const url = `${API_URL}/public/certificates/verify/${code}/pdf`;
    window.open(url, "_blank");
  };

  return (
    <div className="flex min-h-dvh flex-col bg-background text-foreground">
      {/* Platform branding header */}
      <header className="border-b border-border">
        <div className="mx-auto flex h-16 max-w-3xl items-center justify-between gap-3 px-4 md:px-6">
          <Link
            to="/"
            aria-label="Taskpart home"
            className="rounded-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          >
            <BrandMark />
          </Link>
          <span className="eyebrow">Certificate verification</span>
        </div>
      </header>

      <main className="flex flex-1 justify-center px-4 py-10 md:py-16">
        <div className="w-full max-w-xl">
          <AnimatePresence mode="wait">
            {isLoading ? (
              <motion.div
                key="loading"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className="rounded-md border border-border bg-card px-6 py-14 text-center"
                role="status"
              >
                <Loader2 className="mx-auto mb-4 size-6 animate-spin text-muted-foreground" />
                <p className="text-sm text-body">Verifying certificate authenticity...</p>
              </motion.div>
            ) : isError || !data ? (
              <motion.div
                key="error"
                initial={{ opacity: 0, y: 8 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.3 }}
                className="rounded-md border border-border bg-card px-6 py-12 text-center md:px-10"
              >
                <div className="mx-auto mb-5 flex size-12 items-center justify-center rounded-full border border-destructive/30 bg-destructive/10 text-destructive">
                  <CircleAlert className="size-5" />
                </div>
                <h1 className="text-display-sm text-foreground">Certificate not found</h1>
                <p className="mx-auto mt-3 max-w-sm text-sm leading-6 text-body">
                  The certificate code{" "}
                  <code className="rounded-sm border border-border bg-background px-1 py-px font-mono text-code text-foreground break-all">
                    {code}
                  </code>{" "}
                  could not be verified. It may have been revoked, expired, or does not exist in our
                  records.
                </p>
                <Button variant="outline" asChild className="mt-6">
                  <Link to="/">
                    <ArrowLeft />
                    Go home
                  </Link>
                </Button>
              </motion.div>
            ) : (
              <motion.div
                key="success"
                initial={{ opacity: 0, y: 12 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.4, ease: "easeOut" }}
              >
                {/* Verified banner */}
                <div className="mb-4 flex items-start gap-3 rounded-md border border-success/30 bg-success/10 px-4 py-3">
                  <BadgeCheck className="mt-0.5 size-5 shrink-0 text-success" />
                  <div>
                    <p className="text-sm font-medium text-success">Certificate verified ✓</p>
                    <p className="text-caption text-body">
                      This certificate is authentic and issued by Taskpart Learning Network.
                    </p>
                  </div>
                </div>

                {/* Certificate card */}
                <article className="overflow-hidden rounded-md border border-border bg-card">
                  <div className="flex items-center gap-1.5 border-b border-border px-5 py-2.5 font-mono">
                    <span className="size-2 rounded-full bg-hairline-strong" aria-hidden />
                    <span className="size-2 rounded-full bg-hairline-strong" aria-hidden />
                    <span className="size-2 rounded-full bg-hairline-strong" aria-hidden />
                    <span className="ml-3 truncate text-[11px] text-muted-foreground">
                      {data.cert.cert_number}
                    </span>
                  </div>

                  <div className="p-5 md:p-8">
                    <div className="mb-6 flex flex-wrap items-center gap-2">
                      <Badge variant="outline">{data.template.name}</Badge>
                      <Badge variant="success" dot>
                        Active
                      </Badge>
                    </div>

                    <p className="eyebrow mb-2">Certificate holder</p>
                    <h1 className="text-[28px] leading-[34px] font-normal tracking-[-0.6px] text-foreground md:text-display-md md:leading-10 md:font-normal">
                      {data.cert.student_name}
                    </h1>

                    <dl className="mt-8 grid gap-5 border-t border-border pt-6 sm:grid-cols-2">
                      <DetailRow icon={<GraduationCap />} label="Course completed" value={data.cert.course} />
                      <DetailRow icon={<IdCard />} label="University" value={data.cert.university} />
                      <DetailRow icon={<Calendar />} label="Issue date" value={data.cert.issue_date} mono />
                      <DetailRow
                        icon={<BadgeCheck />}
                        label="Certificate ID"
                        value={data.cert.cert_number}
                        mono
                      />
                    </dl>

                    {data.template.description && (
                      <p className="mt-6 rounded-sm border border-border bg-background px-4 py-3 font-serif text-body-md italic text-body">
                        {data.template.description}
                      </p>
                    )}

                    <div className="mt-8 flex flex-col gap-2 sm:flex-row">
                      <Button onClick={handleDownloadPDF}>
                        <Download />
                        Download PDF certificate
                      </Button>
                      <Button variant="outline" onClick={handleCopy}>
                        {copied ? <Check /> : <Copy />}
                        {copied ? "Link copied!" : "Copy verification link"}
                      </Button>
                    </div>
                  </div>
                </article>

                <p className="mt-6 text-center text-caption text-muted-foreground">
                  This verification is provided by Taskpart Learning Network. If you believe this
                  certificate is fraudulent, please contact us at{" "}
                  <a href="mailto:support@taskpart.com" className="text-body hover:text-foreground">
                    support@taskpart.com
                  </a>
                  .
                </p>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </main>
    </div>
  );
}

function DetailRow({
  icon,
  label,
  value,
  mono,
}: {
  icon: React.ReactNode;
  label: string;
  value: string;
  mono?: boolean;
}) {
  return (
    <div className="flex items-start gap-3">
      <span className="mt-0.5 shrink-0 text-muted-foreground [&_svg]:size-4" aria-hidden>
        {icon}
      </span>
      <div className="min-w-0">
        <dt className="eyebrow mb-1">{label}</dt>
        <dd className={mono ? "font-mono text-code break-all text-foreground" : "text-sm font-medium text-foreground"}>
          {value}
        </dd>
      </div>
    </div>
  );
}
